import api from "./axiosConfig"; // Importa la configuración centralizada de axios

// (2026-05-19) Normalizador de COMPATIBILIDAD del shape de Factura tras el
// rename de modelos (clientes->empresas, cliente_final->cliente). El backend
// devuelve la forma agrupada estandarizada:
//   - comprador (ex cliente_final) en `cliente:{}` + `cliente_id`
//   - emisor sólo como `empresa_id` (el objeto emisor se elimina en el
//     corte limpio); la afiliada/tercero viaja en `tercero:{}`
//   - pago.metodo_pago:{} (antes metodo_pago_nombre/codigo planos)
// Para no editar decenas de componentes/columnas DataTables se re-exponen
// alias legacy: snapshot plano cliente_* desde el nested `cliente`, y la
// afiliada en `empresa` desde `tercero` (columnas admin "Afiliada"). Defensivo.
const _compatFactura = (f) => {
  if (!f || typeof f !== "object") return f;
  if (f.tercero && !f.empresa) f.empresa = f.tercero;
  const cf = f.cliente;
  if (cf && typeof cf === "object") {
    if (f.cliente_nombre == null) f.cliente_nombre = cf.nombre;
    if (f.cliente_rif == null) f.cliente_rif = cf.rif;
    if (f.cliente_direccion == null) f.cliente_direccion = cf.direccion;
    if (f.cliente_telefono == null) f.cliente_telefono = cf.telefono;
    if (f.cliente_email == null) f.cliente_email = cf.email;
    if (f.cliente_tipo == null) f.cliente_tipo = cf.tipo;
    if (f.cliente_tipo_doc_identidad == null)
      f.cliente_tipo_doc_identidad = cf.tipo_doc_identidad;
  }
  if (Array.isArray(f.pagos)) {
    f.pagos.forEach((p) => {
      if (p && p.metodo_pago && typeof p.metodo_pago === "object") {
        if (p.metodo_pago_nombre == null) p.metodo_pago_nombre = p.metodo_pago.nombre;
        if (p.metodo_pago_codigo == null) p.metodo_pago_codigo = p.metodo_pago.codigo;
      }
    });
  }
  return f;
};

const _compatFacturaResp = (data) => {
  try {
    if (Array.isArray(data)) { data.forEach(_compatFactura); return data; }
    if (data && Array.isArray(data.data)) { data.data.forEach(_compatFactura); return data; }
    if (data && Array.isArray(data.resultados)) { data.resultados.forEach(_compatFactura); return data; }
  } catch (_e) { /* noop */ }
  return data;
};

export const getInvoices = async (params = {}) => {
  try {
    const authData = localStorage.getItem("authData");
    if (!authData) return [];

    const { rol } = JSON.parse(authData);

    // Determinar endpoint según rol
    const endpoint = (rol === "admin" || rol == "auditor") ? "/admin/invoices" : "/api/invoices";

    // (2026-05-15) Pass-through de TODOS los filtros provistos. Antes habia
    // un whitelist (page/per_page/tipo_documento/numero_control/desde/hasta/
    // estatus) que descartaba silenciosamente doc_tercero, tercero_id,
    // solo_comision, cliente_rif, zona, correlativo_interno -> esos
    // filtros "no buscaban". El backend ignora params que no soporta, asi
    // que reenviar todo es seguro y arregla todos los filtros de una vez.
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      query.append(k, v);
    });

    const response = await api.get(`${endpoint}?${query.toString()}`);
    return _compatFacturaResp(response.data);
  } catch (error) {
    if (error.response && error.response.status === 404) {
      // No se encontraron facturas
      return [];
    }
    console.error("Error al obtener las facturas:", error);
    throw error;
  }
};

// Consultar un pre-facturas por ID
export const showInvoice = async (id) => {
  try {
    const authData = localStorage.getItem("authData");
    if (authData) {
      const { rol } = JSON.parse(authData);
      var response;
      if (rol == 'admin' || rol == "auditor"){
        response = await api.get(`/admin/invoices/${id}`);
        //response = await api.get(`/pre-invoices/${id}`);
      }else{
        response = await api.get(`/api/invoices/${id}`);
      }
      // (2026-05-19) Normalización completa de compatibilidad (reemplaza
      // el shim parcial previo): alias nested->plano + empresa +
      // metodo_pago planos, para no romper componentes legacy.
      return _compatFactura(response.data);
    }
  } catch (error) {
    console.error("Error al consultar el pre-facturas:", error);
    throw error;
  }
};

// Libro de ventas
export const getSalesBook = async (params = {}) => {
  try {
    const authData = localStorage.getItem("authData");
    if (!authData) return [];

    const { rol } = JSON.parse(authData);

    // Determinar endpoint según rol
    const endpoint = (rol === "admin" || rol == "auditor") ? "/api/reports/libro-ventas" : "/api/reports/libro-ventas";

    // (2026-05-15) Pass-through de todos los filtros (igual que getInvoices):
    // el whitelist periodo/desde/hasta descartaba doc_tercero y tercero_id,
    // por lo que el filtro de tercero del Libro de Ventas no se aplicaba.
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      query.append(k, v);
    });

    const response = await api.get(`${endpoint}?${query.toString()}`);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      // No se encontraron facturas
      return [];
    }
    console.error("Error al obtener el libro ventas:", error);
    return error;
  }
};

// (2026-05-21) Enviar el libro de ventas por correo. El blob del Excel
// se POSTea como multipart/form-data para que el backend lo adjunte tal
// cual al email del destinatario, sin re-generar nada server-side.
//
// Reglas de destinatario (resueltas en backend):
//   - Si params.doc_tercero === 'true' Y params.tercero_id es un id
//     especifico de tercero -> email del tercero (Empresa.email).
//   - En cualquier otro caso -> email de la empresa objetivo
//     (Empresa.email) tomando empresa_id (admin/auditor) o el del JWT.
export const enviarLibroVentasEmail = async ({ blob, filename, params = {}, mensaje = "" }) => {
  try {
    const fd = new FormData();
    fd.append("file", blob, filename || "Libro_de_ventas.xlsx");
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;
      fd.append(k, v);
    });
    if (mensaje) fd.append("mensaje", mensaje);
    const response = await api.post(
      "/api/reports/libro-ventas/enviar-email",
      fd,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  } catch (error) {
    const msg = error?.response?.data?.error || error.message || "Error enviando libro";
    return { exito: false, mensaje: msg };
  }
};

// (2026-05-21) Para admin/auditor: lista de empresas del sistema (para
// el filtro de empresa en el Libro de Ventas). Se usa el endpoint
// existente /admin/empresas (IIS reverse-proxy a Waitress; nota: el
// prefijo es /admin, NO /api/admin -- aprendido el 2026-05-21 cuando
// /api/admin/empresas devolvio 404).
//
// Respuesta del endpoint:
//   { page, per_page, total, total_pages, has_next, has_prev, data: [empresas] }
// Por eso aqui devolvemos response.data.data como array plano.
export const getEmpresasParaLibroVentas = async (extraParams = {}) => {
  try {
    const params = { per_page: '500', activo: 'true', ...extraParams };
    // Stringify por seguridad (URLSearchParams convierte numeros pero
    // las claves booleanas/numericas a veces dan problemas).
    Object.keys(params).forEach((k) => { params[k] = String(params[k]); });
    const query = new URLSearchParams(params);
    const response = await api.get(`/admin/empresas?${query.toString()}`);
    const body = response.data || {};
    if (Array.isArray(body)) return body;
    if (Array.isArray(body.data)) return body.data;
    return [];
  } catch (error) {
    if (error?.response?.status === 403) {
      // Usuario no es admin/auditor: regresa lista vacia silenciosamente.
      return [];
    }
    console.error("Error listando empresas:", error);
    return [];
  }
};

// Formato digital
export const showDocument = async (id) => {
  try {
    var response = await api.get(`/api/documents/factura/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error al facturas:", error);
    throw error;
  }
}

// Registrar impresión
export const recordPrintout = async (id) => {
  try {
    //console.log('createClient-body: ', body);
    const response = await api.post(`/api/invoices/${id}/registrar-impresion`, {});
    return response.data;
  } catch (error) {
    console.error("Error al registrar impresion:", error);
    throw error;
  }
};

// (2026-05-21) Notificacion automatica por correo de la emision de factura.
// El cliente envia el PDF en base64; el backend lo adjunta al correo y
// marca facturas.notificacion_envio=True en caso de exito.
//
// Reglas del backend:
//   To = cliente.email (+ tercero.email si doc_tercero=True)
//   Cc = emisor.email
// Si el cliente no tiene email -> 422 y queda pendiente (sin reintento auto).
// Si SMTP falla -> 502 y queda pendiente (se reintenta en proximo batch).
export const notificarFacturaEmail = async (facturaId, pdfBase64) => {
  try {
    const response = await api.post(
      `/api/invoices/${facturaId}/notificar-email`,
      { pdf_base64: pdfBase64 }
    );
    return response.data; // { estado, exito, mensaje, destinatarios, notificacion_envio }
  } catch (error) {
    const status = error?.response?.status;
    const data = error?.response?.data || {};
    return {
      estado: "error",
      exito: false,
      status,
      mensaje: data.mensaje || data.error || error.message || "Error al notificar",
      destinatarios: data.destinatarios || {},
      notificacion_envio: false,
    };
  }
};

// (2026-05-21) Lista facturas del emisor con notificacion_envio=False.
// Se usa en el batch retry: al crear una factura de tercero, primero se
// intentan reenviar las pendientes anteriores antes de notificar la nueva.
export const getFacturasPendientesNotificacion = async (params = {}) => {
  try {
    const cleaned = {};
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") cleaned[k] = v;
    });
    const response = await api.get("/api/invoices/pendientes-notificacion", {
      params: cleaned,
    });
    return response.data; // { facturas: [...], total: N, empresa_id: X }
  } catch (error) {
    if (error?.response?.status === 404) {
      return { facturas: [], total: 0 };
    }
    console.error("Error al consultar pendientes-notificacion:", error);
    return { facturas: [], total: 0 };
  }
};
